// Yangi restoran ustasi: 6 qadam -> tasdiq -> natija (login, parol, PIN, restoran logini).
// Spec packages/shared buildProvisionSpec() bilan yig'iladi; server uni yana tekshiradi.
import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  LIMIT_UNITS,
  MODULES,
  RESTAURANT_TEMPLATES,
  addonPack,
  buildProvisionSpec,
  getModule,
  moduleAddonPrice,
  quoteMonthly,
  isTemplateAvailable,
  normalizePhone,
  starterForTemplate,
  templateSettings,
  getRoleTemplate,
  getSetting,
} from '@onepos/shared';
import type { ModuleDef, NewRestaurantInput, RestaurantTemplate } from '@onepos/shared';
import { Alert, Badge, Button, Card, Icon, Secret, SelectField, Spinner, Switch, TextField, useAsync } from '@onepos/ui';
import { api } from '../lib/api';
import { MonthlyPreview, addonPricesText, useDiscounts } from '../components/MonthlyPreview';
import { fmtMoney, fmtPhone, limitsText } from '../lib/format';
import { settingValueText } from '../lib/settings';
import type { CopySource, CreateTenantResult, Plan } from '../lib/types';

const STEPS = ['Restoran va ega', 'Tarif', 'Ish modeli', 'Menyu va zal', 'Kirish', 'Tasdiq'] as const;

type ContentMode = 'template' | 'copy' | 'empty';

interface Draft {
  name: string;
  city: string;
  address: string;
  ownerName: string;
  ownerPhone: string;
  planId: string;
  trialDays: string;
  autoSuspend: boolean;
  templateKey: string;
  /** Yoqiladigan barcha bo'limlar: tarifdagilari va tarifdan tashqari (qo'shimcha) */
  modules: string[];
  /** Tarifdagidan ortiq qurilmalar (dona), forma qiymati */
  extra: Record<string, string>;
  content: ContentMode;
  copyFrom: string;
  hallTables: string;
  passwordMode: 'generate' | 'custom';
  password: string;
  login: string;
  loginTouched: boolean;
  pinMode: 'generate' | 'custom';
  pin: string;
}

const INITIAL: Draft = {
  name: '',
  city: '',
  address: '',
  ownerName: '',
  ownerPhone: '',
  planId: '',
  trialDays: '14',
  autoSuspend: true,
  templateKey: '',
  modules: [],
  extra: {},
  content: 'template',
  copyFrom: '',
  hallTables: '8',
  passwordMode: 'generate',
  password: '',
  login: '',
  loginTouched: false,
  pinMode: 'generate',
  pin: '',
};

const LOGIN_RE = /^[a-z0-9][a-z0-9_-]{2,31}$/;

/** "Lavash Markaz (Chilonzor)" -> "lavash-markaz-chilonzor" */
export function loginFromName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[‘’ʻʼ'`]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32)
    .replace(/-+$/g, '');
}

const TEMPLATES = RESTAURANT_TEMPLATES as readonly RestaurantTemplate[];

// Shablonda bo'lmasa ham tarifda bo'lsa — o'zi yoqiladi (mijoz ular uchun to'laydi)
const PLAN_DEFAULT_ON = ['aggregators', 'advisor'];

/** Qurilma qaysi bo'lim bilan ishlaydi: bo'lim yoqilmagan bo'lsa, qo'shimcha qurilma ma'nosiz */
const DEVICE_MODULE: Record<string, string> = { pos: 'sales', kds: 'kds', printer: 'kitchen_printers', queue: 'queue_screen', waiter: 'waiters' };

function modulesFor(t: RestaurantTemplate | undefined, plan: Plan | undefined): string[] {
  if (!plan) return t ? [...t.modules] : [];
  const fromTemplate = t ? t.modules.filter((m) => plan.modules.includes(m)) : ['sales'];
  const extra = PLAN_DEFAULT_ON.filter((m) => plan.modules.includes(m) && getModule(m)?.status === 'available');
  return [...new Set([...fromTemplate, ...extra])];
}

/** Formadagi qo'shimcha qurilmalar: narxi bor va bo'limi yoqilganlari */
function extraOf(d: Draft, plan: Plan | undefined): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(d.extra)) {
    const n = Number(v);
    if (!plan || plan.addon_prices?.[k] === undefined || !d.modules.includes(DEVICE_MODULE[k] ?? k)) continue;
    if (/^\d+$/.test(v) && n > 0) out[k] = n;
  }
  return out;
}

export function NewRestaurantPage() {
  const [step, setStep] = useState(0);
  const [d, setD] = useState<Draft>(INITIAL);
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState<string>();
  const [result, setResult] = useState<CreateTenantResult | null>(null);
  const plans = useAsync(() => api.rpc<Plan[]>('platform_plans'), []);
  const activePlans = (plans.data ?? []).filter((p) => p.is_active);
  const plan = activePlans.find((p) => p.id === d.planId);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setD((prev) => ({ ...prev, [key]: value }));

  const choosePlan = (p: Plan) => {
    setD((prev) => ({
      ...prev,
      planId: p.id,
      modules: modulesFor(TEMPLATES.find((t) => t.key === prev.templateKey), p),
      extra: Object.fromEntries(Object.entries(prev.extra).filter(([k]) => p.addon_prices?.[k] !== undefined)),
    }));
  };

  const chooseTemplate = (t: RestaurantTemplate) => {
    setD((prev) => ({
      ...prev,
      templateKey: t.key,
      modules: modulesFor(t, plan),
      content: starterForTemplate(t.key) ? prev.content : prev.content === 'template' ? 'empty' : prev.content,
    }));
  };

  const input: NewRestaurantInput = {
    name: d.name,
    city: d.city,
    ownerName: d.ownerName,
    ownerPhone: d.ownerPhone,
    planId: d.planId,
    templateKey: d.templateKey,
    branchAddress: d.address,
    trialDays: Number(d.trialDays),
    autoSuspend: d.autoSuspend,
    modules: plan ? d.modules.filter((m) => plan.modules.includes(m)) : d.modules,
    addonModules: plan ? d.modules.filter((m) => !plan.modules.includes(m)) : [],
    extraDevices: extraOf(d, plan),
    content: d.content === 'copy' ? { mode: 'copy', fromTenantId: d.copyFrom } : { mode: d.content },
    hallTables: d.modules.includes('hall') ? Number(d.hallTables) : 0,
    ownerPin: d.pinMode === 'custom' ? d.pin : undefined,
  };
  const built = buildProvisionSpec(input);
  const quote = plan ? quoteMonthly(plan, { extra: extraOf(d, plan), modules: d.modules }) : null;

  // Har bir qadam uchun "keyingisiga o'tish mumkinmi"
  const stepErrors: string[][] = [
    [
      !d.name.trim() ? 'Restoran nomini kiriting' : '',
      !d.ownerName.trim() ? 'Ega ismini kiriting' : '',
      !normalizePhone(d.ownerPhone) ? 'Telefon raqami noto‘g‘ri' : '',
    ].filter(Boolean),
    [!plan ? 'Tarifni tanlang' : '', !/^\d+$/.test(d.trialDays) || Number(d.trialDays) > 180 ? 'Sinov muddati 0–180 kun' : ''].filter(Boolean),
    [
      !d.templateKey ? 'Ish modelini tanlang' : '',
      Object.values(d.extra).some((v) => v !== '' && (!/^\d+$/.test(v) || Number(v) > 999)) ? 'Qo‘shimcha qurilmalar soni 0–999' : '',
    ].filter(Boolean),
    [
      d.content === 'copy' && !d.copyFrom ? 'Nusxa olinadigan restoranni tanlang' : '',
      d.modules.includes('hall') && !(/^\d+$/.test(d.hallTables) && Number(d.hallTables) <= 200) ? 'Stollar soni 0–200' : '',
    ].filter(Boolean),
    [
      !LOGIN_RE.test(loginOf(d)) ? 'Restoran logini: 3–32 belgi, lotin harf, raqam, “-” yoki “_”' : '',
      d.passwordMode === 'custom' && (d.password.length < 8 || d.password.length > 72) ? 'Parol 8–72 belgi' : '',
      d.pinMode === 'custom' && !/^\d{6}$/.test(d.pin) ? 'PIN 6 ta raqam' : '',
    ].filter(Boolean),
    built.ok ? [] : built.errors,
  ];

  const submit = async () => {
    if (!built.ok) return;
    setBusy(true);
    setSubmitError(undefined);
    try {
      const r = await api.invoke<CreateTenantResult>('platform-create-tenant', {
        spec: built.spec,
        login: loginOf(d),
        login_password: d.passwordMode === 'custom' ? d.password : null,
      });
      setResult(r);
      window.scrollTo(0, 0);
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <ResultView
        result={result}
        name={d.name}
        password={d.passwordMode === 'custom' ? d.password : result.login_password}
        pin={d.pinMode === 'custom' ? d.pin : result.owner_pin}
        onAnother={() => {
          setResult(null);
          setD(INITIAL);
          setStep(0);
        }}
      />
    );
  }

  const errors = stepErrors[step] ?? [];
  const last = step === STEPS.length - 1;

  return (
    <>
      <header className="cn-page-head">
        <div>
          <Link to="/restoranlar" className="op-btn op-btn--ghost op-btn--sm cn-back">
            <Icon name="back" /> Restoranlar
          </Link>
          <h1>Yangi restoran</h1>
          <p>Restoran ishlashga tayyor holda ochiladi: menyu, sklad, retseptlar, zal, rollar va restoran logini.</p>
        </div>
      </header>

      <ol className="cn-steps" aria-label="Qadamlar">
        {STEPS.map((s, i) => (
          <li key={s} className={i === step ? 'cn-steps--on' : i < step ? 'cn-steps--done' : ''}>
            <button type="button" disabled={i > step} onClick={() => setStep(i)} aria-current={i === step ? 'step' : undefined}>
              <span className="cn-steps__n">{i < step ? <Icon name="check" /> : i + 1}</span>
              <span className="cn-steps__label">{s}</span>
            </button>
          </li>
        ))}
      </ol>

      <Card>
        <div className="cn-wizard">
          {step === 0 && <StepBasics d={d} set={set} />}
          {step === 1 && (
            <StepPlan d={d} set={set} plans={activePlans} loading={plans.loading} error={plans.error?.message} choose={choosePlan} />
          )}
          {step === 2 && <StepModel d={d} set={set} plan={plan} choose={chooseTemplate} total={quote?.monthly ?? null} />}
          {step === 3 && <StepContent d={d} set={set} />}
          {step === 4 && <StepAccess d={d} set={set} plan={plan} />}
          {step === 5 && <StepReview d={d} plan={plan} />}

          {step > 0 && errors.length > 0 && (
            <Alert tone="warning" title="Davom etishdan oldin">
              <ul className="cn-errors">
                {errors.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            </Alert>
          )}
          {submitError && (
            <Alert tone="danger" title="Restoran ochilmadi">
              {submitError}
            </Alert>
          )}

          <div className="cn-wizard__nav">
            <Button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0 || busy}>
              <Icon name="back" /> Orqaga
            </Button>
            {last ? (
              <Button variant="primary" disabled={!built.ok || busy} onClick={() => void submit()}>
                {busy ? 'Ochilmoqda…' : 'Restoranni ochish'}
              </Button>
            ) : (
              <Button variant="primary" disabled={errors.length > 0} onClick={() => setStep((s) => s + 1)}>
                Keyingi <Icon name="chevron" />
              </Button>
            )}
          </div>
        </div>
      </Card>
    </>
  );
}

type SetFn = <K extends keyof Draft>(key: K, value: Draft[K]) => void;

function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="cn-wsec">
      <h2 className="cn-wsec__title">{title}</h2>
      {hint && <p className="cn-muted cn-wsec__hint">{hint}</p>}
      {children}
    </section>
  );
}

// ---------------------------------------------------------------------------
function StepBasics({ d, set }: { d: Draft; set: SetFn }) {
  const phoneOk = !d.ownerPhone || normalizePhone(d.ownerPhone) !== null;
  return (
    <>
      <Section title="Restoran">
        <div className="cn-form-grid">
          <TextField label="Restoran nomi" value={d.name} onChange={(e) => set('name', e.target.value)} maxLength={120} autoFocus />
          <TextField label="Shahar" value={d.city} onChange={(e) => set('city', e.target.value)} placeholder="Toshkent" />
          <TextField
            label="Manzil (ixtiyoriy)"
            value={d.address}
            onChange={(e) => set('address', e.target.value)}
            hint="Chek sarlavhasida ishlatish mumkin"
          />
        </div>
      </Section>
      <Section title="Ega" hint="Ega bilan bog‘lanish uchun. Ilovaga kirish — restoran logini va ega PIN kodi bilan.">
        <div className="cn-form-grid">
          <TextField label="Ega ismi" value={d.ownerName} onChange={(e) => set('ownerName', e.target.value)} autoComplete="off" />
          <TextField
            label="Telefon raqami"
            type="tel"
            inputMode="tel"
            placeholder="+998 90 123 45 67"
            value={d.ownerPhone}
            onChange={(e) => set('ownerPhone', e.target.value)}
            error={phoneOk ? undefined : 'Masalan: +998 90 123 45 67'}
            autoComplete="off"
          />
        </div>
      </Section>
    </>
  );
}

// ---------------------------------------------------------------------------
function StepPlan({
  d,
  set,
  plans,
  loading,
  error,
  choose,
}: {
  d: Draft;
  set: SetFn;
  plans: Plan[];
  loading: boolean;
  error?: string;
  choose: (p: Plan) => void;
}) {
  return (
    <>
      <Section
        title="Tarif"
        hint="Tarif bo‘limlar va qurilmalar sonini belgilaydi. Tarifdan tashqari bo‘lim va qo‘shimcha qurilmalarni keyingi qadamda qo‘shasiz. Tarifni keyin ham almashtirish mumkin."
      >
        {error && <Alert tone="danger">{error}</Alert>}
        {loading ? (
          <Spinner />
        ) : (
          <div className="cn-choices">
            {plans.map((p) => (
              <button
                key={p.id}
                type="button"
                className={`cn-choice${d.planId === p.id ? ' cn-choice--on' : ''}`}
                aria-pressed={d.planId === p.id}
                onClick={() => choose(p)}
              >
                <span className="cn-choice__title">{p.label}</span>
                <span className="cn-choice__meta op-num">
                  {p.monthly_price === null ? 'narx belgilanmagan' : `${fmtMoney(p.monthly_price)} / oy`}
                </span>
                <span className="cn-choice__desc">
                  {limitsText(p.limits)} · {p.max_staff} xodim
                </span>
                <span className="cn-tags">
                  {p.modules.map((m) => (
                    <span key={m} className="cn-tag">
                      {getModule(m)?.label ?? m}
                    </span>
                  ))}
                </span>
                {addonPricesText(p) && <span className="cn-choice__addons">Qo‘shimcha, oyiga: {addonPricesText(p)}</span>}
              </button>
            ))}
          </div>
        )}
      </Section>
      <Section title="Sinov muddati">
        <div className="cn-row">
          {['0', '7', '14', '30', '180'].map((n) => (
            <button
              key={n}
              type="button"
              className={`cn-chip${d.trialDays === n ? ' cn-chip--on' : ''}`}
              aria-pressed={d.trialDays === n}
              onClick={() => {
                set('trialDays', n);
                // Bepul pilot — avtomatik to'xtatishsiz (Q-064)
                set('autoSuspend', n !== '180');
              }}
            >
              {n === '0' ? 'Sinovsiz' : n === '180' ? '6 oy (bepul pilot)' : `${n} kun`}
            </button>
          ))}
          <TextField
            label="Boshqa (kun)"
            inputMode="numeric"
            value={d.trialDays}
            onChange={(e) => set('trialDays', e.target.value.replace(/\D/g, ''))}
            className="cn-input-sm"
          />
        </div>
        <Switch
          checked={d.autoSuspend}
          onChange={(v) => set('autoSuspend', v)}
          label="Muddat tugasa avtomatik to‘xtatish"
          description="Muddat tugagach restoran 7 kun ogohlantirish bilan ishlaydi, keyin to‘lov qilinmasa — faqat ko‘rish rejimi. Bepul pilot uchun o‘chiq."
        />
      </Section>
    </>
  );
}

// ---------------------------------------------------------------------------
const FLOW_KEYS = ['flow.order_taker', 'flow.payment_timing', 'flow.kitchen_routing', 'flow.order_types', 'flow.service_charge_percent'];

function StepModel({
  d,
  set,
  plan,
  choose,
  total,
}: {
  d: Draft;
  set: SetFn;
  plan: Plan | undefined;
  choose: (t: RestaurantTemplate) => void;
  total: number | null;
}) {
  const tpl = TEMPLATES.find((t) => t.key === d.templateKey);
  const flow = tpl ? templateSettings(tpl) : null;
  // Yoqilsa — bog'liq bo'limlar ham yoqiladi; o'chirilsa — unga bog'liqlari ham o'chadi (masalan Zal -> Ofitsiant)
  const toggle = (key: string, on: boolean) => {
    const next = new Set(d.modules);
    const add = (k: string) => {
      next.add(k);
      for (const r of getModule(k)?.requires ?? []) add(r);
    };
    const remove = (k: string) => {
      next.delete(k);
      for (const m of MODULES as readonly ModuleDef[]) if (next.has(m.key) && m.requires?.includes(k)) remove(m.key);
    };
    if (on) add(key);
    else remove(key);
    set('modules', [...next]);
  };
  const devices = plan
    ? Object.keys(LIMIT_UNITS).filter((k) => plan.addon_prices?.[k] !== undefined && d.modules.includes(DEVICE_MODULE[k] ?? k))
    : [];
  return (
    <>
      <Section title="Ish modeli" hint="Restoran qanday ishlashini tanlang. Keyin har bir sozlamani alohida o‘zgartirish mumkin.">
        <div className="cn-choices">
          {TEMPLATES.map((t) => {
            const ready = isTemplateAvailable(t);
            const missing = plan ? t.modules.filter((m) => !plan.modules.includes(m)) : [];
            return (
              <button
                key={t.key}
                type="button"
                disabled={!ready}
                className={`cn-choice${d.templateKey === t.key ? ' cn-choice--on' : ''}`}
                aria-pressed={d.templateKey === t.key}
                onClick={() => choose(t)}
              >
                <span className="cn-choice__title">
                  {t.label}
                  {!ready && <Badge tone="neutral">keyingi reliz</Badge>}
                </span>
                <span className="cn-choice__desc">{t.description}</span>
                {ready && missing.length > 0 && (
                  <span className="cn-warn" style={{ fontSize: 12.5 }}>
                    Tarifda yo‘q: {missing.map((m) => getModule(m)?.label ?? m).join(', ')} — pastda qo‘shimcha qilib yoqish mumkin
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </Section>

      {tpl && flow && (
        <>
          <Section title="Ish jarayoni" hint="Shablondan. Bu tizimli sozlamalarni faqat platforma o‘zgartiradi.">
            <dl className="op-dl">
              {[...FLOW_KEYS, ...Object.keys(tpl.settings ?? {})].map((k) => {
                const def = getSetting(k);
                return def ? (
                  <div key={k} className="op-dl__row">
                    <dt>{def.label}</dt>
                    <dd>{settingValueText(def, flow[k])}</dd>
                  </div>
                ) : null;
              })}
              <div className="op-dl__row">
                <dt>Xodim rollari</dt>
                <dd>{tpl.roles.map((r) => getRoleTemplate(r)?.label ?? r).join(', ')}</dd>
              </div>
              {tpl.stations.length > 0 && (
                <div className="op-dl__row">
                  <dt>Tayyorlash joylari</dt>
                  <dd>{tpl.stations.map((st) => st.label).join(', ')}</dd>
                </div>
              )}
            </dl>
          </Section>
          <Section
            title="Bo‘limlar"
            hint={`“${plan?.label ?? ''}” tarifidagilar narx ichida. Tarifda yo‘q bo‘limni ham yoqish mumkin: narxi qo‘yilgan bo‘lsa — oylik summaga qo‘shiladi, qo‘yilmagan bo‘lsa — bepul beriladi.`}
          >
            <ul className="cn-settings">
              {(MODULES as readonly ModuleDef[]).map((m) => {
                const inPlan = plan?.modules.includes(m.key) ?? false;
                const ready = m.status === 'available';
                const on = d.modules.includes(m.key);
                const price = plan ? moduleAddonPrice(plan, m.key) : null;
                const disabled = m.alwaysOn === true || !ready;
                return (
                  <li key={m.key} className="cn-settings__row">
                    <div className="cn-settings__main">
                      <Switch
                        label={m.label}
                        checked={on}
                        disabled={disabled}
                        onChange={(v) => toggle(m.key, v)}
                        description={
                          <>
                            {m.description}
                            <span className="cn-tags" style={{ marginTop: 6 }}>
                              {!ready ? (
                                <Badge tone="neutral">tez orada</Badge>
                              ) : inPlan ? (
                                <Badge tone="success">tarifda</Badge>
                              ) : price !== null ? (
                                <Badge tone="info">qo‘shimcha · +{fmtMoney(price)}/oy</Badge>
                              ) : (
                                <Badge tone="warning">tarifda yo‘q · yoqilsa bepul</Badge>
                              )}
                              {m.requires?.length ? (
                                <span className="cn-muted">talab: {m.requires.map((r) => getModule(r)?.label ?? r).join(', ')}</span>
                              ) : null}
                            </span>
                          </>
                        }
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          </Section>
          {plan && (
            <Section
              title="Qo‘shimcha qurilmalar"
              hint={`Tarifda: ${limitsText(plan.limits)}. Ko‘proq kerak bo‘lsa — sonini yozing (faqat narxi qo‘yilgan va bo‘limi yoqilgan qurilmalar).`}
            >
              {devices.length === 0 ? (
                <p className="cn-muted" style={{ margin: 0 }}>
                  Bu tarifda qo‘shimcha qurilma sotilmaydi — kerak bo‘lsa, kattaroq tarifni tanlang.
                </p>
              ) : (
                <ul className="cn-extra">
                  {devices.map((k) => {
                    const pack = addonPack(plan, k);
                    const n = Number(d.extra[k] ?? '') || 0;
                    const packs = Math.ceil(n / pack);
                    return (
                      <li key={k} className="cn-extra__row">
                        <span className="cn-extra__name">
                          {LIMIT_UNITS[k]!.charAt(0).toUpperCase() + LIMIT_UNITS[k]!.slice(1)}
                          <span className="cn-list__sub">
                            tarifda {plan.limits[k] ?? 0} ta · {pack > 1 ? `${pack} talik paket` : 'har biri'} {fmtMoney(plan.addon_prices?.[k] ?? 0)}/oy
                          </span>
                        </span>
                        <input
                          className="cn-limit-input"
                          inputMode="numeric"
                          aria-label={`Qo‘shimcha ${LIMIT_UNITS[k]}`}
                          placeholder="0"
                          value={d.extra[k] ?? ''}
                          onChange={(e) => set('extra', { ...d.extra, [k]: e.target.value.replace(/\D/g, '').slice(0, 3) })}
                        />
                        <span className="cn-extra__sum op-num">
                          {n > 0 ? `${pack > 1 ? `${packs} paket · ` : ''}+${fmtMoney(packs * (plan.addon_prices?.[k] ?? 0))}` : '—'}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
              {total !== null && (
                <p className="cn-wizard__total">
                  Oyiga jami: <strong className="op-num">{fmtMoney(total)}</strong>
                  <span className="cn-muted"> — batafsil oxirgi qadamda</span>
                </p>
              )}
            </Section>
          )}
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
function StepContent({ d, set }: { d: Draft; set: SetFn }) {
  const starter = starterForTemplate(d.templateKey);
  const sources = useAsync(
    () => (d.content === 'copy' ? api.rpc<CopySource[]>('platform_copy_sources') : Promise.resolve([] as CopySource[])),
    [d.content],
  );
  const products = starter ? starter.categories.reduce((n, c) => n + c.products.length, 0) : 0;
  const options: { key: ContentMode; title: string; desc: string; disabled?: boolean }[] = [
    {
      key: 'template',
      title: 'Shablon menyusi',
      desc: starter
        ? `${starter.label}: ${starter.categories.length} kategoriya, ${products} mahsulot` +
          (d.modules.includes('inventory') ? `, ${starter.rawMaterials.length} xom ashyo va retseptlar` : '')
        : 'Bu shablonda tayyor menyu yo‘q',
      disabled: !starter,
    },
    { key: 'copy', title: 'Boshqa restorandan nusxa', desc: 'Menyu, qo‘shimchalar, xom ashyo va retseptlar ko‘chiriladi' },
    { key: 'empty', title: 'Bo‘sh', desc: 'Ega menyuni o‘zi kiritadi (keyin Excel’dan yuklash ham bo‘ladi)' },
  ];
  return (
    <>
      <Section title="Menyu" hint="Narx va nomlarni ega keyin o‘zgartiradi.">
        <div className="cn-choices">
          {options.map((o) => (
            <button
              key={o.key}
              type="button"
              disabled={o.disabled}
              className={`cn-choice${d.content === o.key ? ' cn-choice--on' : ''}`}
              aria-pressed={d.content === o.key}
              onClick={() => set('content', o.key)}
            >
              <span className="cn-choice__title">{o.title}</span>
              <span className="cn-choice__desc">{o.desc}</span>
            </button>
          ))}
        </div>
        {d.content === 'template' && starter && (
          <div className="cn-tags" style={{ marginTop: 12 }}>
            {starter.categories.map((c) => (
              <span key={c.name} className="cn-tag">
                {c.icon} {c.name} · {c.products.length}
              </span>
            ))}
          </div>
        )}
        {d.content === 'copy' && (
          <div style={{ marginTop: 12, maxWidth: 480 }}>
            {sources.loading ? (
              <Spinner />
            ) : (
              <SelectField
                label="Qaysi restorandan"
                value={d.copyFrom}
                onChange={(e) => set('copyFrom', e.target.value)}
                options={[
                  { value: '', label: '— tanlang —' },
                  ...(sources.data ?? []).map((s) => ({
                    value: s.id,
                    label: `${s.name}${s.city ? ` (${s.city})` : ''} — ${s.products} mahsulot`,
                  })),
                ]}
              />
            )}
          </div>
        )}
      </Section>
      {d.modules.includes('hall') && (
        <Section title="Zal" hint="“Asosiy zal” yaratiladi. Zonalar va stollarni ega keyin o‘zgartiradi.">
          <TextField
            label="Stollar soni"
            inputMode="numeric"
            value={d.hallTables}
            onChange={(e) => set('hallTables', e.target.value.replace(/\D/g, ''))}
            className="cn-input-sm"
          />
        </Section>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
function loginOf(d: Draft): string {
  return d.loginTouched ? d.login.trim().toLowerCase() : loginFromName(d.name);
}

function StepAccess({ d, set }: { d: Draft; set: SetFn; plan: Plan | undefined }) {
  return (
    <>
      <Section
        title="Restoran logini"
        hint="Har bir qurilma (kassa, oshxona ekrani, ega telefoni) app.onepos.uz da shu login va parol bilan bir marta kiradi, keyin ega yoki menejer PIN bilan tasdiqlaydi."
      >
        <div style={{ maxWidth: 360 }}>
          <TextField
            label="Login"
            value={loginOf(d)}
            onChange={(e) => {
              set('loginTouched', true);
              set('login', e.target.value.toLowerCase().replace(/\s+/g, '-'));
            }}
            hint="3–32 belgi: lotin harflari, raqam, “-” yoki “_”"
            autoComplete="off"
            spellCheck={false}
          />
        </div>
        <div className="cn-row" style={{ marginTop: 10 }}>
          <Radio name="pw" checked={d.passwordMode === 'generate'} onChange={() => set('passwordMode', 'generate')}>
            Parolni avtomatik yaratish
          </Radio>
          <Radio name="pw" checked={d.passwordMode === 'custom'} onChange={() => set('passwordMode', 'custom')}>
            O‘zim kiritaman
          </Radio>
        </div>
        {d.passwordMode === 'custom' && (
          <div style={{ maxWidth: 360, marginTop: 10 }}>
            <TextField
              label="Restoran paroli"
              value={d.password}
              onChange={(e) => set('password', e.target.value)}
              hint="8–72 belgi"
              autoComplete="new-password"
            />
          </div>
        )}
      </Section>
      <Section
        title="Ega PIN kodi"
        hint="6 raqam. Ega PIN bilan kiradi va birinchi marta shaxsiy parol o‘rnatadi (PIN’dan keyin so‘raladi)."
      >
        <div className="cn-row">
          <Radio name="pin" checked={d.pinMode === 'generate'} onChange={() => set('pinMode', 'generate')}>
            Avtomatik yaratish
          </Radio>
          <Radio name="pin" checked={d.pinMode === 'custom'} onChange={() => set('pinMode', 'custom')}>
            O‘zim kiritaman
          </Radio>
        </div>
        {d.pinMode === 'custom' && (
          <div style={{ maxWidth: 200, marginTop: 10 }}>
            <TextField
              label="PIN"
              inputMode="numeric"
              maxLength={6}
              value={d.pin}
              onChange={(e) => set('pin', e.target.value.replace(/\D/g, ''))}
            />
          </div>
        )}
      </Section>
    </>
  );
}

function Radio({ name, checked, onChange, children }: { name: string; checked: boolean; onChange: () => void; children: ReactNode }) {
  return (
    <label className={`cn-chip${checked ? ' cn-chip--on' : ''}`}>
      <input type="radio" name={name} checked={checked} onChange={onChange} className="op-visually-hidden" />
      {children}
    </label>
  );
}

// ---------------------------------------------------------------------------
function StepReview({ d, plan }: { d: Draft; plan: Plan | undefined }) {
  const tpl = TEMPLATES.find((t) => t.key === d.templateKey);
  const starter = starterForTemplate(d.templateKey);
  const discounts = useDiscounts();
  const label = (m: string) => getModule(m)?.label ?? m;
  const inPlan = d.modules.filter((m) => plan?.modules.includes(m));
  const outside = d.modules.filter((m) => !plan?.modules.includes(m));
  const extra = extraOf(d, plan);
  const devices = plan
    ? Object.keys(LIMIT_UNITS)
        .map((k) => ({ k, n: (plan.limits[k] ?? 0) + (extra[k] ?? 0), x: extra[k] ?? 0 }))
        .filter((r) => r.n > 0)
        .map((r) => `${r.n} ${LIMIT_UNITS[r.k]}${r.x > 0 ? ` (${r.x} qo‘shimcha)` : ''}`)
        .join(', ')
    : '—';
  const content = useMemo(() => {
    if (d.content === 'copy') return 'Boshqa restorandan nusxa';
    if (d.content === 'empty' || !starter) return 'Bo‘sh';
    return `${starter.label} (${starter.categories.reduce((n, c) => n + c.products.length, 0)} mahsulot)`;
  }, [d.content, starter]);
  return (
    <Section title="Tekshiring" hint="“Restoranni ochish” bosilgach hammasi bitta amalda yaratiladi.">
      <dl className="op-dl">
        {[
          ['Restoran', `${d.name}${d.city ? `, ${d.city}` : ''}`],
          ['Ega', `${d.ownerName} · ${fmtPhone(normalizePhone(d.ownerPhone))}`],
          ['Tarif', `${plan?.label ?? '—'} · sinov ${d.trialDays === '0' ? 'yo‘q' : `${d.trialDays} kun`}`],
          ['Muddat tugasa', d.autoSuspend ? 'avtomatik to‘xtatiladi (7 kun muhlatdan keyin)' : 'to‘xtatilmaydi — faqat ogohlantirish'],
          ['Ish modeli', tpl?.label ?? '—'],
          ['Bo‘limlar', inPlan.map(label).join(', ')],
          ['Tarifdan tashqari', outside.length ? outside.map(label).join(', ') : '—'],
          ['Qurilmalar', devices || 'faqat ega qurilmalari'],
          ['Menyu', content],
          ['Zal', d.modules.includes('hall') ? `${d.hallTables} ta stol` : '—'],
          ['Restoran logini', loginOf(d)],
          ['Restoran paroli', d.passwordMode === 'custom' ? 'o‘zingiz kiritgan' : 'avtomatik'],
          ['Ega PIN', d.pinMode === 'custom' ? 'o‘zingiz kiritgan' : 'avtomatik'],
        ].map(([k, v]) => (
          <div key={k} className="op-dl__row">
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
      {plan && (
        <>
          <h3 className="cn-wsec__sub">Oylik summa (sinov muddatidan keyin)</h3>
          <MonthlyPreview
            quote={quoteMonthly(plan, { extra, modules: d.modules })}
            planLabel={plan.label}
            discounts={discounts}
            note="Taxminiy hisob; haqiqiy summa “Obuna” bo‘limida ko‘rinadi (filiallar qo‘shilsa — o‘zgaradi)."
          />
        </>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
function ResultView({
  result,
  name,
  password,
  pin,
  onAnother,
}: {
  result: CreateTenantResult;
  name: string;
  password: string | null;
  pin: string | null;
  onAnother: () => void;
}) {
  const c = result.counts;
  return (
    <>
      <header className="cn-page-head cn-noprint">
        <div>
          <h1>Restoran ochildi</h1>
          <p>
            {name} — {c.products ?? 0} mahsulot, {c.categories ?? 0} kategoriya, {c.raw_materials ?? 0} xom ashyo,{' '}
            {c.tables ?? 0} stol, {c.roles ?? 0} rol.
          </p>
        </div>
        <div className="cn-row">
          <Button onClick={() => window.print()}>
            <Icon name="print" /> Chop etish
          </Button>
          <Link className="op-btn op-btn--primary" to={`/restoranlar/${result.tenant_id}`}>
            Restoranga o‘tish
          </Link>
        </div>
      </header>

      <Alert tone="warning" title="Bu ma’lumotlar faqat hozir ko‘rsatiladi">
        Parol va PIN bazada ochiq saqlanmaydi. Chop eting yoki egaga xavfsiz yo‘l bilan yuboring.
      </Alert>
      {result.login_error && (
        <Alert tone="danger" title="Restoran logini saqlanmadi">
          Restoran yaratildi, lekin login berilmadi ({result.login_error}). Restoran sahifasidagi “Kirish” bo‘limida bering.
        </Alert>
      )}

      <div className="cn-print">
        <h2 className="cn-print__title">ONE POS — {name}</h2>
        <div className="cn-secrets">
          <Secret label="Kirish manzili" value="app.onepos.uz" />
          {result.login && <Secret label="Restoran logini" value={result.login} />}
          {result.login && password && <Secret label="Restoran paroli" value={password} />}
          {pin && <Secret label="Ega PIN kodi" value={pin} hint="Birinchi kirishda ega shaxsiy parol o‘rnatadi" />}
        </div>
      </div>

      <div className="cn-row cn-noprint">
        <Button onClick={onAnother}>
          <Icon name="plus" /> Yana restoran ochish
        </Button>
      </div>
    </>
  );
}
